import React, { useEffect, useState } from 'react';
import { Grid, Button, CircularProgress, Box, IconButton } from '@mui/material';
import {
	getSingleOrgSeasonApi,
	getCardDataQRCardsApi,
	generateRosterApi,
	generateTeamTradingCardsApi,
	generateBinderLabelsApi,
	generateNameLabelsApi,
	generateProgramApi,
	viewTeamTradingCardsApi,
	getTeamProfileZipsApi,
	generateGameSheetApi,
	generateSponsorSheetApi,
    viewRosterApi,
} from '../../api/orgSeasonApi';
import { LinkTile } from '../../shared/linkTile';
import { useAuth0 } from '@auth0/auth0-react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { OrgSeason } from '../../api/models/orgSeason';
import { downloadBase64AsFile } from '../../utils/binaryHelper';
import { ErrorHeader } from '../../shared/errorHeader';

import { BasicSplitButton } from '../../shared/basicSplitButton';
import { Team } from '../../api/models/team';
import { InfoBar } from '../../shared/infoBar';
import { memberNameSort, orderTeamLeaders,memberJerseySort, FormatJerseyNumber } from '../../models/helpers';
import { useAppUser } from '../../providers/appUserContext';
import { useApiCallWithAuth } from '../../hooks/useApiCallWithAuth';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { FileDownload } from '@mui/icons-material';
import { MemberSummary } from '../../api/models/member';
import { BasicDropDown } from '../../shared/basicDropDown';
import { orderByItems } from '../../models/dropDownItemViewModel';

export const TeamPage: React.FC = (): JSX.Element => {
	const [searchParams] = useSearchParams();
	const source = searchParams.get('source');

	const [orgSeason, setOrgSeason] = useState<OrgSeason | null>(null);
	const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);

    const [athletes, setAthletes] = useState<MemberSummary[]>([]);


    const [orderBy, setOrderBy] = useState<string>('name');

	const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [isProcessing, setIsProcessing] = useState<boolean>(false);

	const params = useParams();
	const navigate = useNavigate();

	const { isAuthenticated } = useAuth0();
	const { appUser, getPermission, isAdmin, refreshAppUser } = useAppUser();

	const getOrgSeasonApiRequest = useApiCallWithAuth(getSingleOrgSeasonApi);
	const viewRosterApiRequest = useApiCallWithAuth(viewRosterApi);

	const generateRosterApiRequest = useApiCallWithAuth(generateRosterApi);
	const generateTeamTradingCardsApiRequest = useApiCallWithAuth(generateTeamTradingCardsApi);
	const generateBinderLabelsApiRequest = useApiCallWithAuth(generateBinderLabelsApi);
	const generateNameLabelsApiRequest = useApiCallWithAuth(generateNameLabelsApi);
	const getCardDataQRCardsRequest = useApiCallWithAuth(getCardDataQRCardsApi);
	const generateProgramApiRequest = useApiCallWithAuth(generateProgramApi);
	const viewTeamTradingCardsApiRequest = useApiCallWithAuth(viewTeamTradingCardsApi);

	const viewTeamProfileZipsApiRequest = useApiCallWithAuth(getTeamProfileZipsApi);
	const generateGameSheetApiRequest = useApiCallWithAuth(generateGameSheetApi);
	const generateSponsorSheetApiRequest = useApiCallWithAuth(generateSponsorSheetApi);

	const navigateUrl =
		source === 'photo-capture'
			? `/orgseason/${params.orgSeasonGuid}/photo-capture?team-guid=${params.teamGuid}`
			: `/orgseason/${params.orgSeasonGuid}`;

	useEffect(() => {
		if (!params.orgSeasonGuid) {
			throw Error('OrgSeason Guid is missing');
		}

		if (!params.teamGuid) {
			throw Error('Team Guid is missing');
		}

		console.log(`isAuthenticated: ${isAuthenticated}, isAdmin: ${isAdmin(params.orgSeasonGuid)}`);

		if (!isAuthenticated || !isAdmin(params.orgSeasonGuid)) {
			setErrorMessage('You do not have permission to access this page X');
			return;
		}

		initPage();
	}, [isAuthenticated, isAdmin, params.orgSeasonGuid]);

	const menuItems = [
		{ title: 'Add Member', onClick: addMemberHandler },
		{ title: 'Generate Roster', onClick: generateRosterHandler },
		{ title: 'Generate Cards', onClick: generate9UPCardsHandler },
		{ title: 'Generate Cards - 1 UP', onClick: generate1UPCardsHandler },
		{ title: 'Generate Binder Labels', onClick: generateBinderLabelsHandler },
		{ title: 'Generate Name Labels', onClick: generateNameLabelsHandler },
		{ title: 'Get QR Code Card Data', onClick: getCardDataQRCardsHandler },
		{ title: 'Generate Program', onClick: generateProgramHandler },
		{ title: 'Manage Programs', onClick: openManageProgramHandler },

		{ title: 'Manage Games', onClick: gamePageHandler },

		{ title: 'Download Cards', onClick: download9UPCardsHandler },
		{ title: 'Download Cards - 1 UP', onClick: download1UPCardsHandler },
		{ title: 'Download Profiles', onClick: downloadProfileHandler },
		{ title: 'Download Game Sheet', onClick: generateGameSheetHandler },
		{ title: 'Download Sponsor Sheet', onClick: generateSponsorSheetHandler },
	];

	//verify param for orgSeason id exist and request user role for this org
	async function initPage() {
		await refreshOrgSeason();
	}

	async function refreshOrgSeason() {
		setIsLoading(true);

		try {
			const orgSeasonData = await getOrgSeasonApiRequest.request(params.orgSeasonGuid);
			setOrgSeason(orgSeasonData);

			const matchTeam = orgSeasonData.teams.find((team: Team) => team.id === params.teamGuid);
			if (matchTeam) {
                const sortedAthletes = matchTeam.athletes.sort(memberNameSort);
                setAthletes(sortedAthletes);
				matchTeam.leaders = orderTeamLeaders(matchTeam.leaders);
				setSelectedTeam(matchTeam);
			}
		} catch (error: any) {
			setErrorMessage(`Failed to load organization season: ${error.message}`);
		}
		setIsLoading(false);
	}

	function displayError(error: any) {
		if (error.response?.data) {
			setErrorMessage(error.response.data.message);
		} else {
			setErrorMessage(error.message);
		}
	}

	const adminPageHandler = () => {
		if (isAuthenticated && orgSeason && selectedTeam) {
			navigate(`/orgseason/${orgSeason.id}/team/${selectedTeam.id}/admin`);
		}
	};

	function navigateProfilePhotoPageHandler() {
		if (orgSeason && selectedTeam) {
			navigate(`/orgseason/${orgSeason.id}/photo-capture?team-guid=${params.teamGuid}`);
		}
	}

	async function downloadRosterHandler() {
		setIsProcessing(true);
		try {
			const fileRequest = await viewRosterApiRequest.request(selectedTeam!.id, orderBy);
			downloadBase64AsFile(fileRequest.data, fileRequest.name);
		} catch (error: any) {
			displayError(error);
		}
		setIsProcessing(false);
	}

    function setOrderByHandler(buttonOrder: string) {

        console.log(`Setting order by: ${buttonOrder}`);

      if (!selectedTeam) return;

       let sortedAthletes: MemberSummary[] = [];

       
    if (buttonOrder === 'name') {
        sortedAthletes = [...selectedTeam.athletes].sort(memberNameSort); // Sort by name
    } else if (buttonOrder === 'number') {
        sortedAthletes = [...selectedTeam.athletes].sort(memberJerseySort); // Sort by jersey number
    }

    console.log(sortedAthletes);

    setAthletes(sortedAthletes); // Update the athletes state with the new array
    setOrderBy(buttonOrder); // Update the orderBy state
    
    }

	//Menu handlers

	function addMemberHandler() {
		if (orgSeason && selectedTeam) {
			navigate(`/orgseason/${orgSeason.id}/team/${selectedTeam.id}/add`);
		}
	}

	async function generateRosterHandler() {
		setIsProcessing(true);
		try {
			const fileRequest = await generateRosterApiRequest.request(selectedTeam!.id);
			downloadBase64AsFile(fileRequest.data, fileRequest.name);
		} catch (error: any) {
			displayError(error);
		}
		setIsProcessing(false);
	}

	async function generate9UPCardsHandler() {
		await generateCardsHandler('9UP');
	}

	async function generate1UPCardsHandler() {
		await generateCardsHandler('1UP');
	}

	async function generateCardsHandler(layout: string) {
		setIsProcessing(true);
		try {
			await generateTeamTradingCardsApiRequest.request(selectedTeam!.id, layout);
			
            alert('Cards will be ready for download soon.');

		} catch (error: any) {
			displayError(error);
		}
		setIsProcessing(false);
	}

	async function generateBinderLabelsHandler() {
		setIsProcessing(true);
		try {
			const fileRequest = await generateBinderLabelsApiRequest.request(selectedTeam!.id);
			downloadBase64AsFile(fileRequest.data, fileRequest.name);
		} catch (error: any) {
			displayError(error);
		}
		setIsProcessing(false);
	}

	async function generateNameLabelsHandler() {
		setIsProcessing(true);
		try {
			const fileRequest = await generateNameLabelsApiRequest.request(selectedTeam!.id);
			downloadBase64AsFile(fileRequest.data, fileRequest.name);
		} catch (error: any) {
			displayError(error);
		}
		setIsProcessing(false);
	}

	async function getCardDataQRCardsHandler() {
		setIsProcessing(true);
		try {
			const fileRequest = await getCardDataQRCardsRequest.request(selectedTeam!.id);
			downloadBase64AsFile(fileRequest.data, fileRequest.name);
		} catch (error: any) {
			displayError(error);
		}
		setIsProcessing(false);
	}

	async function generateProgramHandler() {
		setIsProcessing(true);
		try {
			const fileRequest = await generateProgramApiRequest.request(selectedTeam!.id);
			downloadBase64AsFile(fileRequest.data, fileRequest.name);
		} catch (error: any) {
			displayError(error);
		}
		setIsProcessing(false);
	}

	async function openManageProgramHandler() {
		if (orgSeason && selectedTeam) {
			navigate(`/orgseason/${orgSeason.id}/team/${selectedTeam.id}/program`);
		}
	}

    	async function gamePageHandler() {
		if (orgSeason && selectedTeam) {
			navigate(`/orgseason/${orgSeason.id}/team/${selectedTeam.id}/game`);
		}
	}

	async function download9UPCardsHandler() {
		await downloadCardsHandler('9UP');
	}

	async function download1UPCardsHandler() {
		await downloadCardsHandler('1UP');
	}

	async function downloadCardsHandler(layout: string) {
		setIsProcessing(true);
		try {
			const fileRequest = await viewTeamTradingCardsApiRequest.request(selectedTeam!.id, layout);
			downloadBase64AsFile(fileRequest.data, fileRequest.name);
		} catch (error: any) {
			displayError(error);
		}
		setIsProcessing(false);
	}

	async function downloadProfileHandler() {
		setIsProcessing(true);
		try {
			const fileRequest = await viewTeamProfileZipsApiRequest.request(selectedTeam!.id);
			downloadBase64AsFile(fileRequest.data, fileRequest.name);
		} catch (error: any) {
			displayError(error);
		}
		setIsProcessing(false);
	}

	async function generateGameSheetHandler() {
		setIsProcessing(true);
		try {
			const fileRequest = await generateGameSheetApiRequest.request(selectedTeam!.id);
			downloadBase64AsFile(fileRequest.data, fileRequest.name);
		} catch (error: any) {
			displayError(error);
		}
		setIsProcessing(false);
	}

	async function generateSponsorSheetHandler() {
		setIsProcessing(true);
		try {
			const fileRequest = await generateSponsorSheetApiRequest.request(selectedTeam!.id);
			downloadBase64AsFile(fileRequest.data, fileRequest.name);
		} catch (error: any) {
			displayError(error);
		}
		setIsProcessing(false);
	}

	function splitButtons() {
		return (
			<Grid container spacing={2} alignItems="center">
				<Grid item>
                     <BasicDropDown id='orderByList'
                                                                label='order By'
                                                                width={275}
                                                                value={orderBy}
                                                                items={orderByItems}
                                                                onChange={(e) => setOrderByHandler(e.target.value)} />



				</Grid>
                <Grid>
                     <IconButton aria-label="back" onClick={(e) => downloadRosterHandler()} >
                    <FileDownloadIcon fontSize="large" />
                    </IconButton>
                </Grid>
			</Grid>
		);
	}

	function renderHeader() {
		return (
			<Grid container spacing={2} justifyContent="flex-start" alignItems="center">
				<Grid item>
					<h1>{selectedTeam!.name}</h1>
				</Grid>

				{isAuthenticated && (
					<Grid item>
						{' '}
						<Button color="primary" variant="contained" onClick={(e) => adminPageHandler()}>
							Admin
						</Button>
					</Grid>
				)}

				{isAuthenticated && (
					<Grid item>
						<Button color="primary" variant="contained" onClick={(e) => navigateProfilePhotoPageHandler()}>
							Update Profile Photos
						</Button>
					</Grid>
				)}
			</Grid>
		);
	}

	function mainContent() {
		return (
			<Box>
				{renderHeader()}

				{isProcessing && <CircularProgress />}

				{!isProcessing && splitButtons()}

				<h3>Athletes ({selectedTeam?.athletes.length})</h3>
				<Grid container>
					{selectedTeam && athletes &&
						athletes.map((item) => (
							<Grid item>
								<LinkTile
									key={item.id}
									firstName={item.firstName}
									lastName={item.lastName}
									identifier={FormatJerseyNumber(item.jerseyNumber, item.jerseyNumberPadding)}
									linkUrl={
										isAuthenticated
											? `/orgSeason/${params.orgSeasonGuid}/team/${selectedTeam.id}/member/${item.id}`
											: ''
									}
									imageUrl={item.profileLink}
								/>
							</Grid>
						))}
				</Grid>

				<h3>Leaders ({selectedTeam?.leaders.length})</h3>
				<Grid container>
					{selectedTeam &&
						selectedTeam.leaders.map((item) => (
							<Grid item>
								<LinkTile
									key={item.id}
									firstName={item.firstName}
									lastName={item.lastName}
									identifier={item.identifier}
									linkUrl={
										isAuthenticated
											? `/orgSeason/${params.orgSeasonGuid}/team/${selectedTeam.id}/member/${item.id}`
											: ''
									}
									imageUrl={item.profileLink}
								/>
							</Grid>
						))}
				</Grid>
			</Box>
		);
	}

	return (
		<Box>
			<InfoBar menuItems={menuItems} navigateUrl={navigateUrl} />
			<ErrorHeader errorMessage={errorMessage} />
			{isLoading && <CircularProgress />}
			{!isLoading && selectedTeam && mainContent()}
		</Box>
	);
};
